import type { VitalsBucket } from '@ctw/shared/api/fhir/models/vitals-bucket';
import { useCTW } from '@ctw/shared/context/ctw-context';
import { usePatientContext } from '@ctw/shared/context/patient-provider';
import type { EhrWriteResponse } from '@ctw/shared/context/writeback-provider';
import { getApiServiceBaseUrl } from '@ctw/shared/utils/environments';
import type { EhrHooksServiceError } from '@ctw/shared/utils/types';
import { pickBy } from 'lodash-es';
import { useCallback } from 'react';

type SupportedVital = {
  type:
    | 'systolic'
    | 'diastolic'
    | 'temperature'
    | 'pulse'
    | 'respiratoryRate'
    | 'pain'
    | 'o2Saturation'
    | 'bmi'
    | 'height'
    | 'weight'
    | 'headCircumference';
  value: string;
  unit?: string;
};

export type SupportedVitalsResponse = {
  data: {
    type: 'supported-vitals';
    attributes: Array<SupportedVital>;
  };
};

export function useGetSupportedVitals() {
  const { requestContext, ctwFetch } = useCTW();
  const { env, ehr, authToken, builderId } = requestContext;
  const getSupportedVitalsRequest = useCallback(async (): Promise<SupportedVitalsResponse> => {
    const url = `${getApiServiceBaseUrl({ service: 'ehr-hooks', env })}/supported-vitals/${ehr}`;

    const headers: HeadersInit = {
      Authorization: `Bearer ${authToken}`,
      'Content-Type': 'application/json',
      'Zus-Account': builderId,
    };

    const response = await ctwFetch(url, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      throw new Error('Could not retrieve supported vitals.');
    }
    return response.data as SupportedVitalsResponse;
  }, [env, ehr, authToken, ctwFetch, builderId]);

  return getSupportedVitalsRequest;
}

type VitalWritebackObservation = {
  type: SupportedVital['type'];
  value: string;
  unit?: string;
};

export function useVitalWriteback() {
  const { requestContext, ctwFetch } = useCTW();
  const { env } = requestContext;
  const { patientID, systemURL } = usePatientContext();
  if (!(patientID && systemURL)) {
    throw new Error('Patient ID or system URL not found');
  }
  const vitalWritebackRequest = useCallback(
    async (vitalBucket: VitalsBucket): Promise<EhrWriteResponse> => {
      const { builderId } = requestContext;
      if (!builderId) {
        throw new Error('Builder ID not found');
      }

      const observations: Array<VitalWritebackObservation> = [];
      if (vitalBucket.bloodPressure?.systolic) {
        observations.push({
          type: 'systolic',
          value: vitalBucket.bloodPressure?.systolic,
        });
      }
      if (vitalBucket.bloodPressure?.diastolic) {
        observations.push({
          type: 'diastolic',
          value: vitalBucket.bloodPressure?.diastolic,
        });
      }
      if (vitalBucket.temperature) {
        observations.push({
          type: 'temperature',
          value: vitalBucket.temperature?.observation.valueWithoutUnits,
          unit: vitalBucket.temperature?.observation.unit,
        });
      }
      if (vitalBucket.pulse) {
        observations.push({
          type: 'pulse',
          value: vitalBucket.pulse?.observation.valueWithoutUnits,
          unit: vitalBucket.pulse?.observation.unit,
        });
      }
      if (vitalBucket.respiratoryRate) {
        observations.push({
          type: 'respiratoryRate',
          value: vitalBucket.respiratoryRate?.observation.valueWithoutUnits,
          unit: vitalBucket.respiratoryRate?.observation.unit,
        });
      }
      if (vitalBucket.pain) {
        observations.push({
          type: 'pain',
          value: vitalBucket.pain?.observation.valueWithoutUnits,
          unit: vitalBucket.pain?.observation.unit,
        });
      }
      if (vitalBucket.oxygenSaturation) {
        observations.push({
          type: 'o2Saturation',
          value: vitalBucket.oxygenSaturation?.observation.valueWithoutUnits,
          unit: vitalBucket.oxygenSaturation?.observation.unit,
        });
      }
      if (vitalBucket.bmi) {
        observations.push({
          type: 'bmi',
          value: vitalBucket.bmi?.observation.valueWithoutUnits,
          unit: vitalBucket.bmi?.observation.unit,
        });
      }
      if (vitalBucket.height) {
        observations.push({
          type: 'height',
          value: vitalBucket.height?.observation.valueWithoutUnits,
          unit: vitalBucket.height?.observation.unit,
        });
      }
      if (vitalBucket.weight) {
        observations.push({
          type: 'weight',
          value: vitalBucket.weight?.observation.valueWithoutUnits,
          unit: vitalBucket.weight?.observation.unit,
        });
      }
      if (vitalBucket.headCircumference) {
        observations.push({
          type: 'headCircumference',
          value: vitalBucket.headCircumference?.observation.valueWithoutUnits,
          unit: vitalBucket.headCircumference?.observation.unit,
        });
      }

      const body = {
        data: {
          type: 'vitals',
          attributes: pickBy({
            ehrContext: {
              ah_department: requestContext.ehrContext?.ah_department,
            },
            date: vitalBucket.dateTime,
            observations: observations,
          }),
          relationships: {
            patient: {
              data: {
                type: 'fhir/Patient',
                identifierSystemURL: systemURL,
                identifierValue: patientID,
              },
            },
          },
        },
      };

      const url = `${getApiServiceBaseUrl({ service: 'ehr-hooks', env })}/vitals`;

      const response = await ctwFetch(url, {
        method: 'POST',
        body: JSON.stringify(body),
        headers: {
          Authorization: `Bearer ${requestContext.authToken}`,
          'Content-Type': 'application/json',
          'Zus-Account': builderId,
        },
      });

      if (!response.ok) {
        const errorMessage = (response.data as EhrHooksServiceError)?.data?.errors?.message;
        if (errorMessage) {
          throw new Error(errorMessage);
        }

        throw new Error('Failed to write vitals to EHR.');
      }

      return response.data as EhrWriteResponse;
    },
    [env, requestContext, ctwFetch, patientID, systemURL],
  );

  return vitalWritebackRequest;
}
